// Route Optimizer API Service
// Wraps all backend endpoints for the Route Optimizer app.
import apiService from '../../../services/apiService';

const BASE = '/routeoptimizer';

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export async function getCompanies() {
  return apiService.get(`${BASE}/companies`);
}

export async function createCompany(nameOrObj) {
  const name = typeof nameOrObj === 'string' ? nameOrObj : nameOrObj.name;
  return apiService.post(`${BASE}/companies`, { name });
}

export async function deleteCompany(cid) {
  return apiService.delete(`${BASE}/companies/${cid}`);
}

// ---------------------------------------------------------------------------
// Branches
// ---------------------------------------------------------------------------

export async function getBranches(cid) {
  return apiService.get(`${BASE}/companies/${cid}/branches`);
}

export async function createBranch(cid, { name, location, region }) {
  return apiService.post(`${BASE}/companies/${cid}/branches`, { name, location, region });
}

export async function deleteBranch(bid) {
  return apiService.delete(`${BASE}/branches/${bid}`);
}

// ---------------------------------------------------------------------------
// Weeks
// ---------------------------------------------------------------------------

export async function getWeeks(bid) {
  const resp = await apiService.get(`${BASE}/branches/${bid}/weeks`);
  // Handle both old (array) and new (object with weeks + metadata) formats
  if (Array.isArray(resp)) return { weeks: resp };
  return resp;
}

export async function createWeek(bid, nameOrObj) {
  const name = typeof nameOrObj === 'string' ? nameOrObj : nameOrObj.name;
  return apiService.post(`${BASE}/branches/${bid}/weeks`, { name });
}

export async function deleteWeek(wid) {
  return apiService.delete(`${BASE}/weeks/${wid}`);
}

// ---------------------------------------------------------------------------
// Week Details & Settings
// ---------------------------------------------------------------------------

export async function getWeekDetails(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/details`);
}

export async function updateWeekSettings(wid, settings) {
  return apiService.post(`${BASE}/weeks/${wid}/settings`, settings);
}

// ---------------------------------------------------------------------------
// Upload: Addresses & Workers
// ---------------------------------------------------------------------------

export async function uploadAddresses(wid, addresses) {
  return apiService.post(`${BASE}/weeks/${wid}/upload/addresses`, { addresses });
}

export async function uploadAddressesFile(wid, file) {
  const formData = new FormData();
  formData.append('file', file);
  return apiService.post(`${BASE}/weeks/${wid}/upload/addresses`, formData, { timeout: 60000 });
}

export async function uploadWorkers(wid, workers) {
  return apiService.post(`${BASE}/weeks/${wid}/upload/workers`, { workers });
}

export async function uploadWorkersFile(wid, file) {
  const formData = new FormData();
  formData.append('file', file);
  return apiService.post(`${BASE}/weeks/${wid}/upload/workers`, formData, { timeout: 60000 });
}

// ---------------------------------------------------------------------------
// Load Demo Data
// ---------------------------------------------------------------------------

export async function loadDemo(wid, simOrObj) {
  const simulation = typeof simOrObj === 'number' ? simOrObj : simOrObj?.simulation ?? 0;
  return apiService.post(`${BASE}/weeks/${wid}/load-demo`, { simulation });
}

// ---------------------------------------------------------------------------
// Build Distance Matrix
// ---------------------------------------------------------------------------

export async function buildMatrix(wid) {
  return apiService.post(`${BASE}/weeks/${wid}/build-matrix`, {}, { timeout: 30000 });
}

export async function getBuildMatrixStatus(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/build-matrix/status`);
}

// ---------------------------------------------------------------------------
// Optimize
// ---------------------------------------------------------------------------

export async function optimize(wid, { budget_minutes, tier, all_tiers } = {}) {
  return apiService.post(`${BASE}/weeks/${wid}/optimize`, { budget_minutes, tier, all_tiers }, { timeout: 30000 });
}

export async function getOptimizeStatus(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/optimize/status`);
}

// ---------------------------------------------------------------------------
// Results, Geocoding, Geometries (tier-aware)
// ---------------------------------------------------------------------------

export async function getResults(wid, tier) {
  const q = tier ? `?tier=${tier}` : '';
  return apiService.get(`${BASE}/weeks/${wid}/results${q}`);
}

export async function getResultsSummary(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/results/summary`);
}

export async function getGeocode(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/geocode`);
}

export async function getGeometries(wid, tier) {
  const q = tier ? `?tier=${tier}` : '';
  return apiService.get(`${BASE}/weeks/${wid}/geometries${q}`);
}

// ---------------------------------------------------------------------------
// Route Itinerary
// ---------------------------------------------------------------------------

export async function getRouteItinerary(wid, { day, technician }) {
  const params = new URLSearchParams();
  if (day !== undefined) params.append('day', day);
  if (technician !== undefined) params.append('technician', technician);
  return apiService.get(`${BASE}/weeks/${wid}/route-itinerary?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export async function exportWeek(wid) {
  return apiService.get(`${BASE}/weeks/${wid}/export`);
}

// ---------------------------------------------------------------------------
// Quick Start
// ---------------------------------------------------------------------------

export async function quickStart(data) {
  return apiService.post(`${BASE}/quick-start`, data);
}

export async function quickStartBelgrade(data) {
  return apiService.post(`${BASE}/quick-start-belgrade`, data);
}

export async function quickStartMiami(data) {
  return apiService.post(`${BASE}/quick-start-miami`, data);
}

// ---------------------------------------------------------------------------
// Batch Optimize
// ---------------------------------------------------------------------------

export async function batchOptimize(weekIds) {
  return apiService.post(`${BASE}/batch-optimize`, { week_ids: weekIds });
}

export async function getBatchOptimizeStatus(batchId) {
  return apiService.get(`${BASE}/batch-optimize/${batchId}/status`);
}

// ---------------------------------------------------------------------------
// Optimizable Weeks (validation check)
// ---------------------------------------------------------------------------

export async function getOptimizableWeeks(weekIds) {
  return apiService.post(`${BASE}/optimizable-weeks`, { week_ids: weekIds });
}

// ---------------------------------------------------------------------------
// Aliases (match component usage to service function names)
// ---------------------------------------------------------------------------

export const listCompanies = getCompanies;
export const listBranches = getBranches;
export const listWeeks = getWeeks;
export const saveSettings = updateWeekSettings;
export const exportSchedule = exportWeek;



